/**
 * Turning a geocoder answer into site fields.
 *
 * Extracted from `SiteMapPicker` so the desktop picker and the field capture
 * screen share one definition. Two copies of this mapping would drift, and the
 * drift would show up as the same pin producing different addresses depending
 * on which screen captured it.
 */

/** The fields a capture produces. Coordinates are the only guaranteed part. */
export interface CapturedSiteFields {
	latitude: number
	longitude: number
	name?: string
	address?: string
	city?: string
	/**
	 * Never saved — the site model has no county, and a county is not a city.
	 * Carried only so the UI has something to say about a rural result that
	 * came back with no city at all, instead of showing the bare state.
	 */
	county?: string
	state?: string
	postalCode?: string
	googlePlaceId?: string
}

export interface Pin {
	lng: number
	lat: number
}

/** Street line from the structured components both providers return. */
export const streetFrom = (parts: { house_number?: string; road?: string } = {}): string =>
	[parts.house_number, parts.road].filter(Boolean).join(' ').trim()

/**
 * Google Places result → capture fields.
 *
 * The pin wins over the result's own coordinates: if the user dragged the pin
 * after picking an address, the pin is where they actually mean.
 */
export const fieldsFromAddressResult = (result: any, pin: Pin): CapturedSiteFields => ({
	latitude: pin.lat,
	longitude: pin.lng,
	name: result?.name || undefined,
	address: streetFrom(result?.address) || undefined,
	city: result?.address?.city || undefined,
	state: result?.address?.state || undefined,
	postalCode: result?.address?.postcode || undefined,
	googlePlaceId: result?.placeId,
})

/**
 * Photon reverse-geocode result → capture fields. `result` may be null.
 *
 * **A null result is a successful capture, not a failure.** Plenty of the sites
 * this exists for — a pad, a parcel, a corner — have no address to find, and
 * the coordinates are the part the user actually chose. Treating "no address"
 * as an error is what makes a tool unusable for exactly the sites it is for.
 */
export const fieldsFromGeocodeResult = (result: any, pin: Pin): CapturedSiteFields => {
	if (!result?.address) {
		return { latitude: pin.lat, longitude: pin.lng }
	}

	// A street-level match puts the road in `name` and leaves `street` unset.
	// The name of a street IS its address line, not a place name — so it
	// belongs in `address`, and `name` must be cleared. Leaving it set is what
	// turned a pin on 2960 Carey Way into a site called "Carey Way" with no
	// address at all.
	const isStreet = result.type === 'street'
	const road = result.address.road || (isStreet ? result.name : undefined)

	return {
		latitude: pin.lat,
		longitude: pin.lng,
		name: (!isStreet && result.name) || undefined,
		address: streetFrom({ house_number: result.address.house_number, road }) || undefined,
		city: result.address.city || undefined,
		county: result.address.county || undefined,
		state: result.address.state || undefined,
		postalCode: result.address.postcode || undefined,
	}
}

/**
 * A name to suggest when the geocoder gave none.
 *
 * A site must have a name — the API requires it — and making a user think of
 * one while standing in the rain is a poor trade. The street line is the most
 * recognisable fallback; coordinates are the last resort but still better than
 * an empty field that blocks the save.
 */
export const suggestedSiteName = (fields: CapturedSiteFields): string => {
	if (fields.name) return fields.name
	// City, then county, then postcode. Rural and unincorporated results often
	// have no city, and a street name on its own is not a site — there is more
	// than one Carey Way.
	const locality = fields.city || fields.county || fields.postalCode
	if (fields.address) {
		return [fields.address, locality].filter(Boolean).join(', ')
	}
	if (locality) return locality
	return `Site at ${fields.latitude.toFixed(5)}, ${fields.longitude.toFixed(5)}`
}
